/**
 * FiscalYearTab — Cierre de Ejercicio (regularización P&L + apertura siguiente período).
 *
 * Wizard simple: lista de ejercicios → detalle con preview → modal de cierre.
 */
import { formatNumberForTenant } from '@/lib/locale'
import { useMemo, useState } from 'react'

/** Una fila de saldo esperado, con su posición en el array. */
type FilaDeSaldoEsperado = ExpectedBalanceInput & { idx: number }
import { Plus, Loader2, Lock, CheckCircle2, Eye, FileText, Scale, Trash2, Upload } from 'lucide-react'
import {
  useFiscalYears, useCreateFiscalYear, useFiscalYearPreviewClose, useCloseFiscalYear,
  useFiscalYearBalanceDiff, useFiscalYearGenerateAdjustment, useFiscalYearParseBalanceExcel,
  useAccounts,
  type FiscalYear, type ExpectedBalanceInput, type BalanceDiffEntry,
  type FiscalYearPreview,
} from '../hooks/useAccounting'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { Modal } from '@/components/ds/Modal'

const STATUS_LABEL: Record<FiscalYear['status'], string> = {
  open: 'Abierto', closing: 'En cierre', closed: 'Cerrado',
}
const STATUS_CLS: Record<FiscalYear['status'], string> = {
  open: 'bg-success-surface-strong text-success-content',
  closing: 'bg-warning-surface-strong text-warning-content',
  closed: 'bg-surface-muted text-content-muted',
}

function fmtCLP(v: string | number): string {
  return formatNumberForTenant(Number(v), 0)
}

export function FiscalYearTab() {
  const [createOpen, setCreateOpen] = useState(false)
  const [closing, setClosing] = useState<FiscalYear | null>(null)
  const [previewing, setPreviewing] = useState<FiscalYear | null>(null)
  const [reconciling, setReconciling] = useState<FiscalYear | null>(null)
  const { can } = usePermission()
  const toast = useToast()

  const { data, isLoading } = useFiscalYears()
  const fiscalYears = data?.fiscal_years ?? []

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-content-muted">
          Cierra ejercicios contables generando los asientos de regularización de P&L y apertura del año siguiente.
          Al completar, se bloquea automáticamente todo el rango del año cerrado.
        </p>
        {can('accounting.fiscal_year', 'create') && (
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} /> Nuevo ejercicio
          </button>
        )}
      </div>

      <DataTable<FiscalYear>
        columns={[
          { key: 'year', header: 'Año', sortable: true,
            render: fy => <span className="text-content font-mono font-semibold">{fy.year}</span> },
          { key: 'status', header: 'Estado', sortable: true,
            sortValue: fy => STATUS_LABEL[fy.status],
            render: fy => (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_CLS[fy.status]}`}>
                {fy.status === 'closed' ? <Lock size={11} /> : <CheckCircle2 size={11} />}
                {STATUS_LABEL[fy.status]}
              </span>
            ) },
          { key: 'result_account_code', header: 'Cuenta de resultado', sortable: true,
            render: fy => (
              <span className="text-content-muted">
                {fy.result_account_code ? `${fy.result_account_code} — ${fy.result_account_name}` : '—'}
              </span>
            ) },
          { key: 'closed_at', header: 'Cerrado por / cuándo', sortable: true,
            render: fy => (
              <span className="text-xs text-content-subtle">
                {fy.closed_at ? (
                  <>
                    <div>{fy.closed_by_email || '—'}</div>
                    <div>{new Date(fy.closed_at).toLocaleDateString('es-CL')}</div>
                  </>
                ) : '—'}
              </span>
            ) },
          { key: 'acciones', header: 'Acciones', width: 260,
            render: fy => (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setPreviewing(fy)}
                  title="Ver saldos preliminares"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary border border-primary-100 rounded-lg hover:bg-primary-50"
                >
                  <Eye size={12} /> Preview
                </button>
                {fy.status !== 'closed' && can('accounting.fiscal_year', 'edit') && (
                  <button
                    onClick={() => setReconciling(fy)}
                    title="Conciliar con balance oficial y generar voucher de ajuste"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-info-content border border-info-border rounded-lg hover:bg-info-surface"
                  >
                    <Scale size={12} /> Conciliar
                  </button>
                )}
                {fy.status !== 'closed' && can('accounting.fiscal_year', 'approve') && (
                  <button
                    onClick={() => setClosing(fy)}
                    title="Cerrar ejercicio"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white bg-warning-fill rounded-lg hover:bg-warning-fill-hover"
                  >
                    <Lock size={12} /> Cerrar
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={fiscalYears}
        rowKey={fy => fy.id}
        loading={isLoading}
        empty={<EmptyState title="Sin ejercicios registrados." size="sm" />}
        caption="Ejercicios contables y su estado de cierre"
      />

      {createOpen && (
        <CreateFiscalYearModal
          existingYears={fiscalYears.map(f => f.year)}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => toast.success('Ejercicio creado.')}
          onError={m => toast.error(m)}
        />
      )}
      {previewing && (
        <PreviewModal fy={previewing} onClose={() => setPreviewing(null)} />
      )}
      {closing && (
        <CloseModal
          fy={closing}
          onClose={() => setClosing(null)}
          onSuccess={() => toast.success(`Ejercicio ${closing.year} cerrado.`)}
          onError={m => toast.error(m)}
        />
      )}
      {reconciling && (
        <ReconcileModal
          fy={reconciling}
          onClose={() => setReconciling(null)}
          onSuccess={(detail) => toast.success(detail)}
          onError={m => toast.error(m)}
        />
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function CreateFiscalYearModal({ existingYears, onClose, onSuccess, onError }: {
  existingYears: number[]
  onClose: () => void
  onSuccess: () => void
  onError: (m: string) => void
}) {
  const currentYear = new Date().getFullYear()
  const defaultYear = existingYears.includes(currentYear)
    ? Math.max(...existingYears, 0) + 1
    : currentYear
  const [year, setYear] = useState(String(defaultYear))
  const [notes, setNotes] = useState('')
  const create = useCreateFiscalYear()

  const handleSave = () => {
    create.mutate(
      { year: parseInt(year, 10), notes: notes.trim() || undefined },
      {
        onSuccess: () => { onSuccess(); onClose() },
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al crear ejercicio')),
      }
    )
  }

  return (
    <Modal open onClose={onClose} title="Nuevo ejercicio fiscal" size="sm"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={create.isPending || !year}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending && <Loader2 size={14} className="animate-spin" />}
          Crear
        </button>
      </>}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="fiscalyeartab-ano" className="block text-xs font-medium text-content-muted mb-1">Año *</label>
          <input id="fiscalyeartab-ano"
            type="number"
            value={year}
            onChange={e => setYear(e.target.value)}
            min={2000}
            max={2100}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content"
          />
        </div>
        <div>
          <label htmlFor="fiscalyeartab-notas-opcional" className="block text-xs font-medium text-content-muted mb-1">Notas (opcional)</label>
          <textarea id="fiscalyeartab-notas-opcional"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content resize-none"
          />
        </div>
      </div>
    </Modal>
  )
}

function PreviewModal({ fy, onClose }: { fy: FiscalYear; onClose: () => void }) {
  const { data, isLoading } = useFiscalYearPreviewClose(fy.id)
  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><FileText size={16} /> Preview cierre {fy.year}</span>} size="2xl">
      <div className="space-y-6">
        {isLoading ? (
          <div className="text-center py-8"><Loader2 size={20} className="animate-spin inline" /></div>
        ) : !data ? (
          <p className="text-sm text-content-muted">Sin datos.</p>
        ) : (
          <>
            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Regularización P&L ({data.pyl_lines.length} cuentas)</h3>
              <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                <div className="p-2 bg-success-surface rounded">
                  <p className="text-xs text-content-muted">Ingresos totales</p>
                  <p className="font-semibold text-success-content">${fmtCLP(data.pyl_total_revenue)}</p>
                </div>
                <div className="p-2 bg-danger-surface rounded">
                  <p className="text-xs text-content-muted">Gastos totales</p>
                  <p className="font-semibold text-danger-content">${fmtCLP(data.pyl_total_expense)}</p>
                </div>
                <div className={`p-2 rounded ${Number(data.pyl_result) >= 0 ? 'bg-success-surface' : 'bg-danger-surface'}`}>
                  <p className="text-xs text-content-muted">Resultado del ejercicio</p>
                  <p className={`font-semibold ${Number(data.pyl_result) >= 0 ? 'text-success-content' : 'text-danger-content'}`}>
                    ${fmtCLP(data.pyl_result)}
                  </p>
                </div>
              </div>
              <DataTable<FiscalYearPreview['pyl_lines'][number]>
                columns={[
                  { key: 'code', header: 'Código', sortable: true,
                    render: l => <span className="font-mono">{l.code}</span> },
                  { key: 'name', header: 'Cuenta', sortable: true,
                    render: l => <span className="text-content">{l.name}</span> },
                  { key: 'balance', header: 'Saldo', numeric: true, sortable: true,
                    sortValue: l => Number(l.balance),
                    render: l => `$${fmtCLP(l.balance)}` },
                ]}
                rows={data.pyl_lines}
                rowKey={l => l.code}
                minWidth={360}
                caption="Cuentas de resultado que se cierran contra la cuenta de resultado del ejercicio"
              />
            </section>

            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Apertura {fy.year + 1} ({data.opening_lines.length} cuentas)</h3>
              <DataTable<FiscalYearPreview['opening_lines'][number]>
                columns={[
                  { key: 'code', header: 'Código', sortable: true,
                    render: l => <span className="font-mono">{l.code}</span> },
                  { key: 'name', header: 'Cuenta', sortable: true,
                    render: l => <span className="text-content">{l.name}</span> },
                  { key: 'opening_debe', header: 'Debe', numeric: true, sortable: true,
                    sortValue: l => Number(l.opening_debe),
                    render: l => (l.opening_debe !== '0' ? `$${fmtCLP(l.opening_debe)}` : '—') },
                  { key: 'opening_haber', header: 'Haber', numeric: true, sortable: true,
                    sortValue: l => Number(l.opening_haber),
                    render: l => (l.opening_haber !== '0' ? `$${fmtCLP(l.opening_haber)}` : '—') },
                ]}
                rows={data.opening_lines}
                rowKey={l => l.code}
                minWidth={420}
                footer={{
                  opening_debe: <span className="font-semibold">${fmtCLP(data.opening_total_debe)}</span>,
                  opening_haber: <span className="font-semibold">${fmtCLP(data.opening_total_haber)}</span>,
                }}
                caption="Saldos que quedan de apertura para el ejercicio siguiente"
              />
            </section>
          </>
        )}
      </div>
    </Modal>
  )
}

function CloseModal({ fy, onClose, onSuccess, onError }: {
  fy: FiscalYear
  onClose: () => void
  onSuccess: () => void
  onError: (m: string) => void
}) {
  const [resultAccount, setResultAccount] = useState('')
  const close = useCloseFiscalYear(fy.id)
  const { data: accountsData } = useAccounts({ is_active: 'true' })
  const equityAccounts = (accountsData?.accounts || []).filter(
    (a: any) => a.account_type === 'equity' && a.is_imputable
  )

  const handleConfirm = () => {
    if (!resultAccount) return
    close.mutate(
      { result_account: resultAccount },
      {
        onSuccess: () => { onSuccess(); onClose() },
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al cerrar ejercicio')),
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Lock size={16} /> Cerrar ejercicio {fy.year}</span>} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleConfirm}
          disabled={close.isPending || !resultAccount}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-warning-fill text-white rounded-lg hover:bg-warning-fill-hover disabled:opacity-50"
        >
          {close.isPending && <Loader2 size={14} className="animate-spin" />}
          Cerrar ejercicio
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="bg-warning-surface border border-warning-border rounded-lg p-3 text-sm text-warning-content-strong">
          <p className="font-semibold">⚠️ Acción irreversible</p>
          <p className="text-xs mt-1">
            Se generarán los vouchers <strong>CIERRE-PYL-{fy.year}</strong> y <strong>APERTURA-{fy.year + 1}</strong>,
            y se bloqueará automáticamente todo el año {fy.year}. Solo se puede revertir desbloqueando
            el período manualmente y eliminando los vouchers generados.
          </p>
        </div>
        <div>
          <label htmlFor="fiscalyeartab-cuenta-de-resultado-del-ejercicio" className="block text-xs font-medium text-content-muted mb-1">Cuenta de resultado del ejercicio *</label>
          <select id="fiscalyeartab-cuenta-de-resultado-del-ejercicio"
            value={resultAccount}
            onChange={e => setResultAccount(e.target.value)}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content"
          >
            <option value="">— Seleccionar cuenta de patrimonio —</option>
            {equityAccounts.map((a: any) => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-content-subtle">
            Cuenta de tipo Patrimonio donde se acumulará la utilidad o pérdida del ejercicio.
          </p>
        </div>
      </div>
    </Modal>
  )
}

function ReconcileModal({ fy, onClose, onSuccess, onError }: {
  fy: FiscalYear
  onClose: () => void
  onSuccess: (detail: string) => void
  onError: (m: string) => void
}) {
  const [rows, setRows] = useState<ExpectedBalanceInput[]>([
    { code: '', expected_debe: '0', expected_haber: '0' },
  ])
  const [contraparte, setContraparte] = useState('')
  const [glosa, setGlosa] = useState(`Ajuste a balance oficial ${fy.year}`)
  const [diff, setDiff] = useState<BalanceDiffEntry[] | null>(null)

  const balanceDiff = useFiscalYearBalanceDiff(fy.id)
  const generate = useFiscalYearGenerateAdjustment(fy.id)
  const parseExcel = useFiscalYearParseBalanceExcel(fy.id)
  const { data: accountsData } = useAccounts({ is_active: 'true' })
  const equityAccounts = (accountsData?.accounts || []).filter(
    (a: any) => a.account_type === 'equity' && a.is_imputable
  )

  const addRow = () => setRows(r => [...r, { code: '', expected_debe: '0', expected_haber: '0' }])
  const removeRow = (idx: number) => setRows(r => r.filter((_, i) => i !== idx))
  const updateRow = (idx: number, key: keyof ExpectedBalanceInput, value: string) => {
    setRows(r => r.map((row, i) => i === idx ? { ...row, [key]: value } : row))
  }

  /**
   * Las filas con su POSICIÓN adentro.
   *
   * ⚠️ `updateRow` y `removeRow` reciben el índice en el array, y el `render`
   * del `DataTable` recibe el de la fila YA ORDENADA. Con el índice viajando
   * dentro del dato los dos coinciden siempre, así que marcar una columna
   * `sortable` deja de poder escribir en la fila equivocada.
   *
   * 🔴 Y aun así estas columnas NO llevan `sortable`, a propósito: el orden de
   * las filas es lo que el contador escribió, no una vista de los datos.
   */
  const filasDeSaldo = useMemo<FilaDeSaldoEsperado[]>(
    () => rows.map((r, idx) => ({ ...r, idx })),
    [rows],
  )

  const validRows = rows.filter(r => r.code && r.code.trim())

  const handleCompare = () => {
    balanceDiff.mutate(
      { expected_balances: validRows },
      {
        onSuccess: (data) => setDiff(data.diff),
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al calcular diff')),
      }
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    parseExcel.mutate(
      { file },
      {
        onSuccess: (data) => {
          if (data.error) {
            onError(data.error)
            return
          }
          if (data.rows.length === 0) {
            onError('El archivo no contiene filas válidas. Revisa el header y la hoja.')
            return
          }
          setRows(data.rows.map(r => ({
            code: r.code, expected_debe: r.expected_debe, expected_haber: r.expected_haber,
          })))
          setDiff(null)
        },
        onError: (err: any) => onError(extractErrorMessage(err, 'Error al parsear Excel')),
      }
    )
    e.target.value = ''
  }

  const handleGenerate = () => {
    if (!contraparte) return
    generate.mutate(
      { expected_balances: validRows, contraparte_account: contraparte, glosa },
      {
        onSuccess: (data: any) => { onSuccess(data.detail || 'Voucher generado'); onClose() },
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al generar voucher')),
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Scale size={16} /> Conciliar con balance oficial — {fy.year}</span>} size="2xl">
      <div className="space-y-5">
        <p className="text-xs text-content-muted">
          Carga los saldos del balance oficial (del contador externo) y compáralos contra los calculados por el sistema.
          El diff queda asentado en un voucher <strong>AJU-{fy.year}</strong> en estado <em>borrador</em> para que el contador apruebe.
        </p>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-content">Saldos esperados</h3>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 cursor-pointer">
              {parseExcel.isPending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Importar de Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                disabled={parseExcel.isPending}
              />
            </label>
          </div>
          <DataTable<FilaDeSaldoEsperado>
            columns={[
              { key: 'code', header: 'Código cuenta *',
                render: f => (
                  <input
                    value={f.code || ''}
                    onChange={e => updateRow(f.idx, 'code', e.target.value)}
                    placeholder="ej. 1101"
                    aria-label={`Código de cuenta de la fila ${f.idx + 1}`}
                    className="w-full px-2 py-1 text-xs font-mono border border-input-border rounded bg-input-bg text-content"
                  />
                ) },
              { key: 'expected_debe', header: 'Debe (acum)', numeric: true,
                render: f => (
                  <input
                    type="number"
                    step="0.01"
                    value={f.expected_debe}
                    onChange={e => updateRow(f.idx, 'expected_debe', e.target.value)}
                    aria-label={`Debe acumulado de la fila ${f.idx + 1}`}
                    className="w-full px-2 py-1 text-xs text-right font-mono border border-input-border rounded bg-input-bg text-content"
                  />
                ) },
              { key: 'expected_haber', header: 'Haber (acum)', numeric: true,
                render: f => (
                  <input
                    type="number"
                    step="0.01"
                    value={f.expected_haber}
                    onChange={e => updateRow(f.idx, 'expected_haber', e.target.value)}
                    aria-label={`Haber acumulado de la fila ${f.idx + 1}`}
                    className="w-full px-2 py-1 text-xs text-right font-mono border border-input-border rounded bg-input-bg text-content"
                  />
                ) },
              { key: 'quitar', header: '', width: 32,
                render: f => (
                  <div className="text-center">
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(f.idx)}
                        title="Quitar fila"
                        className="p-1 hover:bg-soft rounded text-content-subtle hover:text-danger-solid">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ) },
            ]}
            rows={filasDeSaldo}
            rowKey={f => f.idx}
            caption="Saldos esperados por cuenta, para contrastar contra el mayor"
          />
          <button
            onClick={addRow}
            className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Plus size={12} /> Agregar cuenta
          </button>
          <div className="mt-3 flex justify-end">
            <button
              onClick={handleCompare}
              disabled={balanceDiff.isPending || validRows.length === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded hover:bg-primary-hover disabled:opacity-50"
            >
              {balanceDiff.isPending ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
              Comparar saldos
            </button>
          </div>
        </section>

        {diff && diff.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-content mb-2">Diferencias detectadas</h3>
            <DataTable<BalanceDiffEntry>
              columns={[
                { key: 'code', header: 'Cuenta', sortable: true,
                  render: d => (<><span className="font-mono">{d.code}</span> · {d.name}</>) },
                { key: 'calculated_net', header: 'Calculado (neto)', numeric: true, sortable: true,
                  sortValue: d => Number(d.calculated_net), render: d => fmtCLP(d.calculated_net) },
                { key: 'expected_net', header: 'Esperado (neto)', numeric: true, sortable: true,
                  sortValue: d => Number(d.expected_net), render: d => fmtCLP(d.expected_net) },
                { key: 'delta_net', header: 'Δ', numeric: true, sortable: true,
                  sortValue: d => Number(d.delta_net),
                  render: d => (
                    <span className={`font-semibold ${Number(d.delta_net) === 0 ? 'text-content-muted' : Number(d.delta_net) > 0 ? 'text-success-content-soft' : 'text-danger-content-soft'}`}>
                      {Number(d.delta_net) === 0 ? '—' : fmtCLP(d.delta_net)}
                    </span>
                  ) },
                { key: 'ajuste', header: 'Ajuste', numeric: true,
                  render: d => (
                    <span className="text-xs text-content-muted">
                      {Number(d.adjust_debe) > 0 && `+D ${fmtCLP(d.adjust_debe)}`}
                      {Number(d.adjust_haber) > 0 && `+H ${fmtCLP(d.adjust_haber)}`}
                      {Number(d.adjust_debe) === 0 && Number(d.adjust_haber) === 0 && '—'}
                    </span>
                  ) },
              ]}
              rows={diff}
              rowKey={d => d.account_id}
              minWidth={560}
              caption="Diferencias entre el saldo calculado y el del balance oficial"
            />
          </section>
        )}

        {diff && diff.some(d => Number(d.delta_net) !== 0) && (
          <section className="border-t border-divider pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-content">Generar voucher de ajuste</h3>
            <div>
              <label htmlFor="fiscalyeartab-cuenta-de-contrapartida" className="block text-xs font-medium text-content-muted mb-1">Cuenta de contrapartida *</label>
              <select id="fiscalyeartab-cuenta-de-contrapartida"
                value={contraparte}
                onChange={e => setContraparte(e.target.value)}
                className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content"
              >
                <option value="">— Seleccionar cuenta de patrimonio —</option>
                {equityAccounts.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-content-subtle">
                Típicamente "Resultado del Ejercicio" o "Ajustes de Ejercicios Anteriores".
              </p>
            </div>
            <div>
              <label htmlFor="fiscalyeartab-glosa" className="block text-xs font-medium text-content-muted mb-1">Glosa</label>
              <input id="fiscalyeartab-glosa"
                value={glosa}
                onChange={e => setGlosa(e.target.value)}
                className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleGenerate}
                disabled={generate.isPending || !contraparte}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-info-fill text-white rounded-lg hover:bg-info-fill-hover disabled:opacity-50"
              >
                {generate.isPending && <Loader2 size={14} className="animate-spin" />}
                Generar voucher de ajuste (draft)
              </button>
            </div>
          </section>
        )}
      </div>
    </Modal>
  )
}
